import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];

const EMPTY_COCHE = {
  nombre: '', km_actual: '', km_proxima_revision: '',
  itv_caducidad: '', itv_proxima_cita: '', seguro_caducidad: '', notas: '',
};
const EMPTY_EVENTO = { tipo: 'mantenimiento', fecha_inicio: '', fecha_fin: '', descripcion: '' };

const TIPO_EVENTO_LABELS = {
  mantenimiento: { icon: '🔧', label: 'Mantenimiento',   color: '#60a5fa' },
  cambio_aceite: { icon: '🛢️', label: 'Cambio de aceite', color: '#fb923c' },
  no_disponible: { icon: '🚫', label: 'No disponible',   color: '#f87171' },
  itv_cita:      { icon: '🛂', label: 'Cita ITV',         color: '#a78bfa' },
  otro:          { icon: '📌', label: 'Otro',             color: '#facc15' },
};

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function fmtFecha(str) {
  if (!str) return '—';
  return new Date(str + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}
function daysUntil(str) {
  if (!str) return null;
  const today = new Date(todayStr() + 'T12:00:00');
  const target = new Date(str + 'T12:00:00');
  return Math.round((target - today) / 86400000);
}
function normNombre(s) {
  return (s || '').trim().toLowerCase();
}

async function downloadDocumento(doc) {
  try {
    if (doc.cloudinary_url) {
      const newWin = window.open('', '_blank');
      const { url } = await api.get(`/api/endesa/${doc.id}/signed-url`);
      if (url) { if (newWin) { newWin.location.href = url; return; } window.open(url, '_blank', 'noreferrer'); return; }
      if (newWin) newWin.close();
    }
    const blob = await api.get(`/api/endesa/${doc.id}/file`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    document.body.appendChild(a);
    a.href = blobUrl; a.download = doc.file_name || 'documento'; a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch (e) { alert('No se pudo descargar: ' + (e.message || e)); }
}

export default function Coches() {
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const [coches, setCoches] = useState([]);
  const [docsByVehicle, setDocsByVehicle] = useState({}); // normNombre -> { nombre, docs: [] }
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [eventoModal, setEventoModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);

  useEffect(() => {
    if (user && !ROLES_ADMIN.includes(user.rol)) navigate('/');
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, eRes] = await Promise.all([
        api.get('/api/coches'),
        api.get('/api/endesa/docs').catch(() => ({ data: [] })),
      ]);
      setCoches(cRes.data || []);
      const byVehicle = {};
      (eRes.data || []).filter(d => d.seccion === 'coches' && d.subseccion).forEach(d => {
        const key = normNombre(d.subseccion);
        if (!byVehicle[key]) byVehicle[key] = { nombre: d.subseccion, docs: [], qr: null };
        if (d.doc_tipo === 'qr' && !byVehicle[key].qr) byVehicle[key].qr = d;
        else byVehicle[key].docs.push(d);
      });
      setDocsByVehicle(byVehicle);
    } catch {
      setCoches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Vehículos registrados en /api/coches + vehículos que solo existen como
  // documentos subidos en ENDESA → Vehículos (aún sin ficha propia)
  const vehiculos = (() => {
    const registrados = coches.map(c => ({
      ...c,
      docs: docsByVehicle[normNombre(c.nombre)]?.docs || [],
      qr:   docsByVehicle[normNombre(c.nombre)]?.qr   || null,
    }));
    const nombresRegistrados = new Set(coches.map(c => normNombre(c.nombre)));
    const soloEndesa = Object.entries(docsByVehicle)
      .filter(([key]) => !nombresRegistrados.has(key))
      .map(([, v]) => ({
        id: null, nombre: v.nombre, km_actual: null, km_proxima_revision: null,
        itv_caducidad: null, itv_proxima_cita: null, notas: null, eventos: [], docs: v.docs, qr: v.qr,
      }));
    return [...registrados, ...soloEndesa].sort((a, b) => a.nombre.localeCompare(b.nombre));
  })();

  function setField(field, val) {
    setModal(m => ({ ...m, data: { ...m.data, [field]: val } }));
  }
  function setEventoField(field, val) {
    setEventoModal(m => ({ ...m, data: { ...m.data, [field]: val } }));
  }

  async function saveCoche() {
    setSaving(true);
    try {
      const d = modal.data;
      const payload = {
        nombre: d.nombre,
        km_actual: d.km_actual === '' ? null : parseInt(d.km_actual, 10),
        km_proxima_revision: d.km_proxima_revision === '' ? null : parseInt(d.km_proxima_revision, 10),
        itv_caducidad: d.itv_caducidad || null,
        itv_proxima_cita: d.itv_proxima_cita || null,
        seguro_caducidad: d.seguro_caducidad || null,
        notas: d.notas || null,
      };
      if (modal.mode === 'add') await api.post('/api/coches', payload);
      else await api.put(`/api/coches/${d.id}`, payload);
      setModal(null); await load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function delCoche(coche) {
    if (!window.confirm(`¿Eliminar la ficha del vehículo ${coche.nombre}? Se borrarán también sus eventos (los documentos de ENDESA no se tocan).`)) return;
    try { await api.delete(`/api/coches/${coche.id}`); await load(); }
    catch (e) { alert('Error: ' + e.message); }
  }

  async function saveEvento() {
    setSaving(true);
    try {
      const d = eventoModal.data;
      const payload = {
        tipo: d.tipo,
        fecha_inicio: d.fecha_inicio,
        fecha_fin: d.fecha_fin || null,
        descripcion: d.descripcion || null,
      };
      if (eventoModal.mode === 'add') await api.post(`/api/coches/${eventoModal.cocheId}/eventos`, payload);
      else await api.put(`/api/coches/eventos/${d.id}`, payload);
      setEventoModal(null); await load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function delEvento(eventoId) {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try { await api.delete(`/api/coches/eventos/${eventoId}`); await load(); }
    catch (e) { alert('Error: ' + e.message); }
  }

  async function delDocumento(docId) {
    if (!window.confirm('¿Eliminar este documento?')) return;
    try { await api.delete(`/api/endesa/${docId}`); await load(); }
    catch (e) { alert('Error: ' + e.message); }
  }

  // Los vehículos que solo existen como documentos de ENDESA no tienen fila propia
  // en /api/coches todavía; al añadirles el primer evento se crea automáticamente.
  async function ensureCocheId(coche) {
    if (coche.id) return coche.id;
    const r = await api.post('/api/coches', { nombre: coche.nombre });
    await load();
    return r.data.id;
  }

  async function abrirNuevoEvento(coche) {
    try {
      const id = await ensureCocheId(coche);
      setEventoModal({ mode: 'add', cocheId: id, data: { ...EMPTY_EVENTO } });
    } catch (e) { alert('Error: ' + e.message); }
  }

  // Alterna disponibilidad sin tener que crear/editar un evento a mano:
  // si hay algún evento "no_disponible" activo ahora mismo lo cierra (fecha_fin
  // = hoy); si no, crea uno abierto desde hoy. Reutiliza el mismo sistema de
  // eventos que ya decide el badge "NO DISPONIBLE", así no queda un evento
  // abierto "fantasma" que ya no aparece en "próximos eventos" pero sigue
  // marcando el coche como no disponible para siempre.
  async function toggleDisponible(coche) {
    try {
      const id = await ensureCocheId(coche);
      const today = todayStr();
      const activos = (coche.eventos || []).filter(e =>
        e.tipo === 'no_disponible' && e.fecha_inicio <= today && (!e.fecha_fin || e.fecha_fin >= today)
      );
      if (activos.length) {
        for (const ev of activos) {
          await api.put(`/api/coches/eventos/${ev.id}`, { fecha_fin: today, descripcion: ev.descripcion || null });
        }
      } else {
        await api.post(`/api/coches/${id}/eventos`, { tipo: 'no_disponible', fecha_inicio: today });
      }
      await load();
    } catch (e) { alert('Error: ' + e.message); }
  }

  async function subirDocumento(nombreVehiculo, file, reset) {
    setUploadingFor(nombreVehiculo);
    try {
      const sign = await api.get('/api/endesa/sign-upload');
      const { timestamp, folder, signature, api_key, cloud_name } = sign;
      const ext = (file.name || '').split('.').pop().toLowerCase();
      const IMAGE_EXTS = new Set(['jpg','jpeg','png','gif','webp','bmp','tiff','svg']);
      const resourceType = IMAGE_EXTS.has(ext) ? 'image' : 'raw';
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', api_key);
      fd.append('timestamp', String(timestamp));
      fd.append('signature', signature);
      fd.append('folder', folder);
      const cldRes = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/${resourceType}/upload`, { method: 'POST', body: fd });
      if (!cldRes.ok) {
        const err = await cldRes.json().catch(() => ({}));
        throw new Error(err?.error?.message || `Cloudinary error ${cldRes.status}`);
      }
      const cld = await cldRes.json();
      await api.post('/api/endesa/metadata', {
        seccion: 'coches', subseccion: nombreVehiculo,
        file_name: file.name, cloudinary_url: cld.secure_url, cloudinary_id: cld.public_id,
      });
      reset();
      await load();
    } catch (e) { alert('Error al subir: ' + e.message); }
    finally { setUploadingFor(null); }
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--text-dim)', textAlign: 'center' }}>Cargando…</div>;

  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>🚗 Coches</div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{vehiculos.length} vehículo{vehiculos.length !== 1 ? 's' : ''}</div>
        </div>
        <button style={BTN_PRI} onClick={() => setModal({ mode: 'add', data: { ...EMPTY_COCHE } })}>+ Añadir vehículo</button>
      </div>

      {vehiculos.length === 0 ? (
        <div style={{ color: 'var(--text-dim)', fontSize: 13, textAlign: 'center', padding: 40 }}>
          Sin vehículos todavía. Añade uno aquí o sube su primer documento en ENDESA → Vehículos.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 16 }}>
          {vehiculos.map(c => <CocheCard key={c.id ?? `endesa-${c.nombre}`} coche={c}
            uploading={uploadingFor === c.nombre}
            onEdit={() => setModal({ mode: c.id ? 'edit' : 'add', data: {
              id: c.id, nombre: c.nombre,
              km_actual: c.km_actual ?? '', km_proxima_revision: c.km_proxima_revision ?? '',
              itv_caducidad: c.itv_caducidad ? c.itv_caducidad.slice(0,10) : '',
              itv_proxima_cita: c.itv_proxima_cita ? c.itv_proxima_cita.slice(0,10) : '',
              seguro_caducidad: c.seguro_caducidad ? c.seguro_caducidad.slice(0,10) : '',
              notas: c.notas || '',
            } })}
            onDelete={c.id ? () => delCoche(c) : null}
            onToggleDisponible={() => toggleDisponible(c)}
            onAddEvento={() => abrirNuevoEvento(c)}
            onEditEvento={ev => setEventoModal({ mode: 'edit', cocheId: c.id, data: {
              id: ev.id, tipo: ev.tipo, fecha_inicio: ev.fecha_inicio?.slice(0,10) || '',
              fecha_fin: ev.fecha_fin ? ev.fecha_fin.slice(0,10) : '', descripcion: ev.descripcion || '',
            } })}
            onDeleteEvento={delEvento}
            onDeleteDocumento={delDocumento}
            onUpload={(file, reset) => subirDocumento(c.nombre, file, reset)}
          />)}
        </div>
      )}

      {/* ── Modal coche ──────────────────────────────────────────────────── */}
      {modal && (
        <div style={OVERLAY} onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div style={MODAL}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>
              {modal.mode === 'add' ? 'Añadir vehículo' : 'Editar vehículo'}
            </div>
            <Fld label="Matrícula / nombre *">
              <input style={INP} list="vehiculos-endesa" value={modal.data.nombre}
                onChange={e => setField('nombre', e.target.value)} placeholder="Ej: 1234 ABC" />
              <datalist id="vehiculos-endesa">
                {vehiculos.map(v => <option key={v.nombre} value={v.nombre} />)}
              </datalist>
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="KM actual">
                <input style={INP} type="number" min="0" value={modal.data.km_actual}
                  onChange={e => setField('km_actual', e.target.value)} />
              </Fld>
              <Fld label="KM próxima revisión">
                <input style={INP} type="number" min="0" value={modal.data.km_proxima_revision}
                  onChange={e => setField('km_proxima_revision', e.target.value)} />
              </Fld>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Caducidad ITV">
                <input style={INP} type="date" value={modal.data.itv_caducidad}
                  onChange={e => setField('itv_caducidad', e.target.value)} />
              </Fld>
              <Fld label="Próxima cita ITV">
                <input style={INP} type="date" value={modal.data.itv_proxima_cita}
                  onChange={e => setField('itv_proxima_cita', e.target.value)} />
              </Fld>
            </div>
            <Fld label="Caducidad seguro">
              <input style={INP} type="date" value={modal.data.seguro_caducidad}
                onChange={e => setField('seguro_caducidad', e.target.value)} />
            </Fld>
            <Fld label="Notas">
              <textarea style={{ ...INP, minHeight: 60, resize: 'vertical' }} value={modal.data.notas}
                onChange={e => setField('notas', e.target.value)} />
            </Fld>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={{ ...BTN_PRI, flex: 1 }} onClick={saveCoche} disabled={saving || !modal.data.nombre.trim()}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button style={BTN_SEC} onClick={() => setModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal evento ─────────────────────────────────────────────────── */}
      {eventoModal && (
        <div style={OVERLAY} onClick={e => e.target === e.currentTarget && setEventoModal(null)}>
          <div style={MODAL}>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>
              {eventoModal.mode === 'add' ? 'Añadir evento' : 'Editar evento'}
            </div>
            <Fld label="Tipo">
              <select style={INP} value={eventoModal.data.tipo} onChange={e => setEventoField('tipo', e.target.value)}>
                {Object.entries(TIPO_EVENTO_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </Fld>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <Fld label="Fecha inicio *">
                <input style={INP} type="date" value={eventoModal.data.fecha_inicio}
                  onChange={e => setEventoField('fecha_inicio', e.target.value)} />
              </Fld>
              <Fld label="Fecha fin (opcional)">
                <input style={INP} type="date" value={eventoModal.data.fecha_fin}
                  onChange={e => setEventoField('fecha_fin', e.target.value)} />
              </Fld>
            </div>
            <Fld label="Descripción">
              <textarea style={{ ...INP, minHeight: 60, resize: 'vertical' }} value={eventoModal.data.descripcion}
                onChange={e => setEventoField('descripcion', e.target.value)} />
            </Fld>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button style={{ ...BTN_PRI, flex: 1 }} onClick={saveEvento} disabled={saving || !eventoModal.data.fecha_inicio}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button style={BTN_SEC} onClick={() => setEventoModal(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Convierte la URL de Cloudinary del QR en una miniatura JPG real (página 1,
// recortada a cuadrado) en vez del icono genérico de archivo — funciona igual
// si el QR se subió como imagen o como PDF, siempre que se subiera como
// resource_type "image" (ver doUpload en Endesa.js).
function qrPreviewUrl(cloudinaryUrl) {
  if (!cloudinaryUrl || !cloudinaryUrl.includes('/image/upload/')) return null;
  return cloudinaryUrl.replace('/image/upload/', '/image/upload/w_200,h_200,c_fit,pg_1,f_jpg,q_auto/');
}

// Miniatura del QR del vehículo: intenta mostrar el propio QR como imagen;
// si no se puede (p. ej. se subió como PDF "raw" antes de este cambio),
// cae al icono de archivo genérico y al enlace de descarga firmado.
function QrThumb({ qr }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const preview = qrPreviewUrl(qr.cloudinary_url);
  useEffect(() => {
    let alive = true;
    api.get(`/api/endesa/${qr.id}/signed-url`).then(r => { if (alive) setUrl(r.url); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [qr.id]);
  const box = { width: 88, height: 88, borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  if (err) return <div style={{ ...box, background: 'var(--surface-deep)', border: '1px solid var(--border)', fontSize: 10, color: 'var(--text-dim)', textAlign: 'center', padding: 6 }}>QR no disponible</div>;
  if (!url) return <div style={{ ...box, background: 'var(--surface-deep)', border: '1px solid var(--border)' }} />;
  return (
    <a href={url} target="_blank" rel="noreferrer" style={{ ...box, textDecoration: 'none', background: '#fff', border: '1px solid var(--border)', overflow: 'hidden' }}>
      {preview && !imgErr
        ? <img src={preview} alt="QR del vehículo" onError={() => setImgErr(true)} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4, boxSizing: 'border-box' }} />
        : <span style={{ fontSize: 28 }}>📄</span>}
    </a>
  );
}

function CocheCard({ coche, onEdit, onDelete, onToggleDisponible, onAddEvento, onEditEvento, onDeleteEvento, onDeleteDocumento, onUpload, uploading }) {
  const today = todayStr();
  const eventos = coche.eventos || [];
  const docs = coche.docs || [];
  const kmHistorial = coche.km_historial || [];
  const fileRef = useRef(null);
  const [showHistorial, setShowHistorial] = useState(false);
  const noDisponibleAhora = eventos.some(e =>
    e.tipo === 'no_disponible' && e.fecha_inicio <= today && (!e.fecha_fin || e.fecha_fin >= today)
  );
  const proximos = eventos
    .filter(e => !e.fecha_fin ? e.fecha_inicio >= today : e.fecha_fin >= today)
    .sort((a, b) => a.fecha_inicio.localeCompare(b.fecha_inicio));

  const itvDias = daysUntil(coche.itv_caducidad);
  const itvColor = itvDias == null ? 'var(--text)' : itvDias < 0 ? '#f87171' : itvDias <= 30 ? '#facc15' : 'var(--text)';

  const seguroDias = daysUntil(coche.seguro_caducidad);
  const seguroColor = seguroDias == null ? 'var(--text)' : seguroDias < 0 ? '#f87171' : seguroDias <= 30 ? '#facc15' : 'var(--text)';

  const kmActual = coche.km_actual;
  const kmRevision = coche.km_proxima_revision;
  const kmColor = (kmActual != null && kmRevision != null && kmActual >= kmRevision) ? '#f87171'
    : (kmActual != null && kmRevision != null && kmRevision - kmActual <= 1000) ? '#facc15' : 'var(--text)';

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        {coche.qr
          ? <QrThumb qr={coche.qr} />
          : <div style={{ width: 88, height: 88, borderRadius: 8, flexShrink: 0, background: 'var(--surface-deep)', border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--text-dim)', textAlign: 'center', padding: 6 }}>
              Sin QR (súbelo en ENDESA → Vehículos)
            </div>}

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--text)' }}>🚗 {coche.nombre}</span>
              {!coche.id && (
                <span style={{ fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 4, background: 'rgba(96,165,250,.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,.3)' }}>
                  SIN FICHA
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={onToggleDisponible} title={noDisponibleAhora ? 'Pulsa para marcar disponible' : 'Pulsa para marcar no disponible'} style={{
                background: noDisponibleAhora ? 'rgba(248,113,113,.1)' : 'rgba(74,222,128,.1)',
                border: noDisponibleAhora ? '1px solid rgba(248,113,113,.3)' : '1px solid rgba(74,222,128,.3)',
                color: noDisponibleAhora ? '#f87171' : '#4ade80',
                padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
              }}>{noDisponibleAhora ? '🚫 No disponible' : '✅ Disponible'}</button>
              <button onClick={onEdit} style={{ background: 'rgba(245,158,11,.1)', border: '1px solid rgba(245,158,11,.3)', color: 'var(--amber)', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>✏</button>
              {onDelete && <button onClick={onDelete} style={{ background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', color: '#f87171', padding: '4px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>🗑</button>}
            </div>
          </div>
        </div>
      </div>

      {!coche.id ? (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 12 }}>
          Este vehículo solo tiene documentos en ENDESA. Pulsa ✏ para añadir KM y ITV, o "+ Añadir evento" abajo para su primer mantenimiento/cita.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <Stat label="KM actual" val={kmActual != null ? kmActual.toLocaleString('es-ES') : '—'} color={kmColor} />
          <Stat label="KM próx. revisión" val={kmRevision != null ? kmRevision.toLocaleString('es-ES') : '—'} color={kmColor} />
          <Stat label="Caducidad ITV" val={fmtFecha(coche.itv_caducidad)} color={itvColor} />
          <Stat label="Próxima cita ITV" val={fmtFecha(coche.itv_proxima_cita)} color="var(--text)" />
          <Stat label="Caducidad seguro" val={fmtFecha(coche.seguro_caducidad)} color={seguroColor} />
        </div>
      )}

      {coche.notas && (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 10, fontStyle: 'italic' }}>{coche.notas}</div>
      )}

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>
            Próximos eventos
          </div>
          {proximos.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8 }}>Sin eventos programados</div>
          ) : (
            <div style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
              {proximos.map(ev => {
                const tc = TIPO_EVENTO_LABELS[ev.tipo] || TIPO_EVENTO_LABELS.otro;
                return (
                  <div key={ev.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 9px', minWidth: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 3, background: `${tc.color}20`, color: tc.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {tc.icon} {tc.label}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {fmtFecha(ev.fecha_inicio)}{ev.fecha_fin ? ` → ${fmtFecha(ev.fecha_fin)}` : ''}
                    </span>
                    {ev.descripcion && <span style={{ fontSize: 11, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{ev.descripcion}</span>}
                    <button onClick={() => onEditEvento(ev)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}>✏</button>
                    <button onClick={() => onDeleteEvento(ev.id)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}>✕</button>
                  </div>
                );
              })}
            </div>
          )}
          <button onClick={onAddEvento} style={{ background: 'var(--surface2)', color: 'var(--text-dim)', border: '1px dashed var(--border)', padding: '6px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 11, fontWeight: 700, width: '100%' }}>
            + Añadir evento
          </button>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <div style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>
          Documentos ({docs.length})
        </div>
        {docs.length === 0 ? (
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8 }}>Sin documentos</div>
        ) : (
          <div style={{ display: 'grid', gap: 6, marginBottom: 8 }}>
            {docs.map(d => {
              const ext = (d.file_name || '').split('.').pop().toUpperCase();
              const icon = ext === 'PDF' ? '📄' : ['JPG','JPEG','PNG'].includes(ext) ? '🖼️' : '📎';
              return (
                <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 9px', minWidth: 0 }}>
                  <span style={{ fontSize: 13, flexShrink: 0 }}>{icon}</span>
                  <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{d.file_name || 'documento'}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-dim)', flexShrink: 0 }}>{new Date(d.created_at).toLocaleDateString('es-ES')}</span>
                  <button onClick={() => downloadDocumento(d)} style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}>↓</button>
                  <button onClick={() => onDeleteDocumento(d.id)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 11, flexShrink: 0 }}>✕</button>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" ref={fileRef}
            style={{ fontSize: 11, color: 'var(--text-dim)', flex: 1, minWidth: 140, background: 'var(--surface-deep)', border: '1px dashed var(--border)', borderRadius: 6, padding: '6px 8px' }} />
          <button
            onClick={() => { if (!fileRef.current?.files?.length) return; onUpload(fileRef.current.files[0], () => { fileRef.current.value = ''; }); }}
            disabled={uploading}
            style={{ background: 'var(--amber)', color: '#000', border: 'none', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontWeight: 800, fontSize: 11, flexShrink: 0 }}>
            {uploading ? '…' : '+ Subir'}
          </button>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 10 }}>
        <button onClick={() => setShowHistorial(v => !v)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
          <span style={{ fontSize: 10, color: 'var(--amber)', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            Historial ({kmHistorial.length})
          </span>
          <span style={{ color: 'var(--text-dim)', fontSize: 11, marginLeft: 'auto' }}>{showHistorial ? '▲' : '▼'}</span>
        </button>
        {showHistorial && (
          kmHistorial.length === 0 ? (
            <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>Sin reportes de km todavía</div>
          ) : (
            <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
              {kmHistorial.map(h => (
                <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-deep)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 9px', minWidth: 0 }}>
                  <span style={{ fontSize: 11, color: 'var(--text)', fontWeight: 700, flexShrink: 0 }}>{h.km.toLocaleString('es-ES')} km</span>
                  <span style={{ fontSize: 11, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{h.user_nombre || 'Usuario desconocido'}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-dim)', flexShrink: 0 }}>
                    {new Date(h.created_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function Stat({ label, val, color }) {
  return (
    <div>
      <div style={{ fontSize: 9, color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color }}>{val}</div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={LBL}>{label}</label>
      {children}
    </div>
  );
}

const INP = { background: 'var(--surface-deep)', border: '1.5px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const LBL = { fontSize: 10, color: 'var(--text-dim)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', display: 'block', marginBottom: 4 };
const BTN_PRI = { background: 'var(--amber)', color: '#000', border: 'none', padding: '9px 18px', borderRadius: 7, cursor: 'pointer', fontWeight: 800, fontSize: 13 };
const BTN_SEC = { background: 'var(--surface2)', color: 'var(--text-dim)', border: '1px solid var(--border)', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const CARD = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 16px', minWidth: 0, overflow: 'hidden' };
const OVERLAY = { position: 'fixed', inset: 0, background: '#000a', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 500 };
const MODAL = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 22, width: '90%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' };
